import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Users, Shield, UserCheck, UserX, Trash2, Circle, Clock, KeyRound } from 'lucide-react';
import { confirmAction } from '@/lib/confirm';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsersPage() {
  const { isAdmin, loading: authLoading, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [resetTarget, setResetTarget] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/');
      return;
    }
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin, authLoading, navigate]);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`);
      setUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const approveUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/approve`, {});
      toast.success('User approved!');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to approve user');
    }
  };

  const declineUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/decline`, {});
      toast.success('User declined');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to decline user');
    }
  };

  const deleteUser = async (userId) => {
    if (!(await confirmAction('Are you sure you want to delete this user?', { confirmLabel: 'Delete' }))) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const updateRole = async (userId, role) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role });
      toast.success('Role updated');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update role');
    }
  };

  const resetUserPassword = async (userId, name) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/reset-password`);
      toast.success(`${name}'s password has been reset to default`);
      setResetTarget(null);
    } catch (err) {
      toast.error('Failed to reset password');
    }
  };

  const filteredUsers = users.filter(u => {
    if (filter === 'all') return true;
    if (filter === 'pending') return u.status === 'pending';
    if (filter === 'approved') return u.status === 'approved';
    if (filter === 'online') return u.online;
    return true;
  });

  const stats = {
    total: users.length,
    pending: users.filter(u => u.status === 'pending').length,
    online: users.filter(u => u.online).length,
  };

  if (authLoading || loading) {
    return (
      <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505]" data-testid="admin-users-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-8 h-8 text-cyan-400" />
              <h1 className="font-heading text-3xl font-bold uppercase text-white">
                User Accounts
              </h1>
            </div>
            <p className="text-zinc-500">
              Manage all registered users
            </p>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-black border-zinc-800 text-white rounded-none" data-testid="user-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-zinc-800 rounded-none">
              <SelectItem value="all" className="text-white">All Users</SelectItem>
              <SelectItem value="pending" className="text-white">Pending</SelectItem>
              <SelectItem value="approved" className="text-white">Approved</SelectItem>
              <SelectItem value="online" className="text-white">Online</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-[#121212] border border-zinc-800 p-4">
            <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Total Users</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="bg-[#121212] border border-zinc-800 p-4">
            <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Pending Approval</p>
            <p className="text-2xl font-bold text-yellow-500">{stats.pending}</p>
          </div>
          <div className="bg-[#121212] border border-zinc-800 p-4">
            <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Online Now</p>
            <p className="text-2xl font-bold text-green-500">{stats.online}</p>
          </div>
        </div>

        {/* Users List */}
        {filteredUsers.length === 0 ? (
          <div className="text-center py-16 bg-[#121212] border border-zinc-800">
            <Users className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No users found</p>
          </div>
        ) : (
          <div className="bg-[#121212] border border-zinc-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500">Status</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500">User</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500">Email</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500">Role</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500">Account</th>
                    <th className="text-right p-4 text-xs uppercase tracking-wider text-zinc-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="border-b border-zinc-800/50 hover:bg-zinc-900/50" data-testid={`user-row-${user.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {user.online ? (
                            <Circle className="w-3 h-3 fill-green-500 text-green-500" />
                          ) : (
                            <Circle className="w-3 h-3 text-zinc-600" />
                          )}
                          <span className="text-xs text-zinc-500">
                            {user.online ? 'Online' : 'Offline'}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                            <span className="text-sm text-cyan-400 font-bold">
                              {user.name?.charAt(0).toUpperCase() || '?'}
                            </span>
                          </div>
                          <span className="text-white font-medium">{user.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-zinc-400 text-sm">{user.email}</td>
                      <td className="p-4">
                        {user.id !== currentUser?.id ? (
                          <Select
                            value={user.role}
                            onValueChange={(role) => updateRole(user.id, role)}
                          >
                            <SelectTrigger className="w-24 h-8 bg-black border-zinc-800 text-white rounded-none text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121212] border-zinc-800 rounded-none">
                              <SelectItem value="user" className="text-white">User</SelectItem>
                              <SelectItem value="admin" className="text-white">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="flex items-center gap-1 text-yellow-500 text-sm">
                            <Shield className="w-4 h-4" />
                            {user.role}
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        {user.status === 'pending' ? (
                          <span className="flex items-center gap-1 text-yellow-500 text-xs">
                            <Clock className="w-3 h-3" />
                            Pending
                          </span>
                        ) : user.status === 'declined' ? (
                          <span className="text-red-500 text-xs">Declined</span>
                        ) : (
                          <span className="text-green-500 text-xs">Approved</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          {user.status === 'pending' && (
                            <>
                              <Button
                                onClick={() => approveUser(user.id)}
                                className="bg-green-500 text-black hover:bg-green-400 rounded-none h-8 px-3"
                                size="sm"
                                data-testid={`approve-${user.id}`}
                              >
                                <UserCheck className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => declineUser(user.id)}
                                variant="outline"
                                className="border-red-500/50 text-red-500 hover:bg-red-500/10 rounded-none h-8 px-3"
                                size="sm"
                                data-testid={`decline-${user.id}`}
                              >
                                <UserX className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {user.id !== currentUser?.id && (
                            <>
                              <Button
                                onClick={() => setResetTarget({ id: user.id, name: user.name })}
                                variant="ghost"
                                className="text-cyan-400/70 hover:text-cyan-400 hover:bg-cyan-400/10 rounded-none h-8 px-3 gap-1"
                                size="sm"
                                title="Reset password to default"
                                data-testid={`reset-pw-${user.id}`}
                              >
                                <KeyRound className="w-4 h-4" />
                                <span className="text-[10px] uppercase tracking-wide hidden xl:inline">Reset PW</span>
                              </Button>
                              <Button
                                onClick={() => deleteUser(user.id)}
                                variant="ghost"
                                className="text-zinc-500 hover:text-red-500 rounded-none h-8 px-3"
                                size="sm"
                                data-testid={`delete-${user.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Reset Password Confirmation Dialog */}
      <Dialog open={!!resetTarget} onOpenChange={() => setResetTarget(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="reset-pw-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white uppercase">Reset Password</DialogTitle>
          </DialogHeader>
          {resetTarget && (
            <div className="mt-4 space-y-4">
              <p className="text-zinc-400 text-sm">
                Reset <span className="text-white font-bold">{resetTarget.name}</span>'s password to the default <span className="text-cyan-400 font-mono">"Password"</span>?
              </p>
              <p className="text-zinc-500 text-xs">The user will be prompted to change their password on next login.</p>
              <div className="flex gap-3">
                <Button onClick={() => setResetTarget(null)} variant="outline" className="flex-1 border-zinc-700 text-zinc-400 hover:text-white rounded-none h-10">
                  Cancel
                </Button>
                <Button
                  onClick={() => resetUserPassword(resetTarget.id, resetTarget.name)}
                  className="flex-1 bg-cyan-400 text-black font-bold uppercase hover:bg-cyan-300 rounded-none h-10"
                  data-testid="confirm-reset-pw"
                >
                  Reset Password
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
